<template>
  <DSForm
    ref="dsForm"
    class="boxed-container boxed-container--vertical"
    scope="{ formData }"
    :prefill-form-data="prefillFormData"
    @submit="handleSubmit"
  >
    <fieldset>
      <DSSelect
        v-model="formData.award_id"
        :value="formData.award_id"
        :options="awardOptions"
        :has-groups="true"
        label="Nominee Award Category"
        class="left"
      />

      <DSSelect
        v-if="hasSizes"
        v-model="formData.organization_size_name"
        :use-name-as-value="true"
        :value="formData.organization_size_name"
        :options="orgSizeOptions"
        label="Select Organization Size"
      />
    </fieldset>
    <hr>
    <template v-if="nomineeType">
      <template v-if="nomineeType === 'Person'">
        <fieldset>
          <DSInput
            v-model="formData.nominee_first_name"
            :value="formData.nominee_first_name"
            label="Nominee's First Name (What is the name of the person you are nominating)"
            class="left"
          />
          <DSInput
            v-model="formData.nominee_last_name"
            :value="formData.nominee_last_name"
            label="Nominee's Last Name"
          />
          <DSInput
            v-model="formData.nominee_email"
            :value="formData.nominee_email"
            label="Nominee's Email"
            type="email"
            rules="email"
            class="left"
          />
          <DSInput
            v-model="formData.url"
            :value="formData.url"
            label="Nominee's Website"
            :required="false"
            rules=""
          />
          <DSInput
            v-model="formData.nominee_organization"
            :value="formData.nominee_organization"
            label="Nominee's Organization"
            :required="false"
            rules=""
            class="full-width"
          />
        </fieldset>

        <hr>

        <fieldset>
          <DSInput
            v-model="formData.nominee_address"
            :value="formData.nominee_address"
            label="Nominee's Address"
            class="full-width"
          />
          <DSInput
            v-model="formData.nominee_city"
            :value="formData.nominee_city"
            label="Nominee's City"
            class="left"
          />
          <!--TODO: Make label dynamic based on country selection-->
          <DSRegionSelect
            v-model="formData.nominee_state"
            :country="formData.nominee_country"
            label="Nominee's State/Province/Region"
          />
          <DSInput
            v-model="formData.nominee_zip_code"
            :value="formData.nominee_zip_code"
            label="Nominee's Zip Code"
            class="left"
          />
          <DSCountrySelect
            v-model="formData.nominee_country"
            label="Nominee's Country/Region"
          />
        </fieldset>

        <hr>
      </template>
      <template v-else-if="nomineeType === 'Organization'">
        <fieldset>
          <DSInput
            v-model="formData.nominee_organization"
            :value="formData.nominee_organization"
            label="Nominee Organization Name (What is the name of the organization you are nominating)"
            :required="true"
            rules=""
            class="full-width"
          />
        </fieldset>

        <hr>
        <fieldset>
          <DSInput
            v-model="formData.nominee_address"
            :value="formData.nominee_address"
            label="Nominee Organization Address"
            class="full-width"
          />
          <DSInput
            v-model="formData.nominee_city"
            :value="formData.nominee_city"
            label="Nominee Organization City"
            class="left"
          />
          <!--TODO: Make label dynamic based on country selection-->
          <DSRegionSelect
            v-model="formData.nominee_state"
            :country="formData.nominee_country"
            label="Nominee Organization State/Province/Region"
          />
          <DSInput
            v-model="formData.nominee_zip_code"
            :value="formData.nominee_zip_code"
            label="Nominee Organization Zip Code"
            class="left"
          />
          <DSCountrySelect
            v-model="formData.nominee_country"
            label="Nominee Organization Country/Region"
          />

          <DSInput
            v-model="formData.url"
            :value="formData.url"
            label="Nominee Organization Website"
            :required="true"
            class="left"
            rules=""
          />
        </fieldset>

        <hr>

        <fieldset>
          <DSInput
            v-model="formData.nominee_first_name"
            :value="formData.nominee_first_name"
            label="Nominee Organization Contact Person First Name"
            class="left"
          />
          <DSInput
            v-model="formData.nominee_last_name"
            :value="formData.nominee_last_name"
            label="Nominee Organization Contact Person Last Name"
          />
          <DSInput
            v-model="formData.nominee_email"
            :value="formData.nominee_email"
            label="Nominee Organization Contact Person Email"
            type="email"
            rules="email"
            class="left"
          />
        </fieldset>
        <hr>
      </template>

      <fieldset>
        <p>
          Grant recipients are selected from Purple Ribbon Award winners. If the award winner is not a non-profit organization, grants will awarded to the nominee's associated non-profit organization, as indicted here. If the nominee is a non-profit organization, repeat their name and website here.
        </p>
        <DSInput
          v-model="formData.non_profit_name"
          :value="formData.non_profit_name"
          label="Associated Nonprofit Organization Name"
          :required="true"
          rules=""
          class="left"
        />
        <DSInput
          v-model="formData.non_profit_website"
          :value="formData.non_profit_website"
          :required="true"
          rules=""
          label="Associated Nonprofit Organization Website"
        />
      </fieldset>

      <hr>

      <fieldset>
        <DSTextArea
          v-model="formData.description"
          :value="formData.description"
          rules="required|maxWords:525|displayMaxWords:500"
          label="Description of Nominee"
          placeholder="Use this field to briefly summarize the nominee and reasons for nomination. Why are you nominating this individual, team or organization? What success have they achieved? What difference have they made? How have they helped?"
          additional-info="Please describe how your nominee has made a difference in the domestic violence field personally, locally, nationally or globally. (Max 500 words)"
        />
      </fieldset>

      <hr>

      <h3 class="narrative-questions-heading">Narrative Questions</h3>

      <p>
        Answers should demonstrate how the nominee provides excellence in relation to each subject. <strong>Do not duplicate or copy answers</strong>. Each field should be answered with unique information addressing the subject. Nominations will be judged by this narrative as well as by attached supporting files and the overall excellence of the nominee.
      </p>
      <fieldset>
        <DSTextArea
          v-model="formData.innovation"
          :value="formData.innovation"
          label="Innovation"
          additional-info="Describe how this nominee demonstrates creativity and originality or has innovated in the domestic violence field. (Max 500 words)"
          rules="required|maxWords:525|displayMaxWords:500"
        />
        <DSTextArea
          v-model="formData.hope_story"
          :value="formData.hope_story"
          label="Hope Story"
          additional-info="Share a story that demonstrates how the nominee overcame challenges or the positive impact of the nominee's efforts. (Max 500 words)"
          rules="required|maxWords:525|displayMaxWords:500"
        />
        <DSTextArea
          v-model="formData.outcomes"
          :value="formData.outcomes"
          label="Outcomes"
          additional-info="Describe the quantifiable outcomes of this nominee's work, such as data showing meaningful results and number of survivors favorably impacted. (Max 500 words)"
          rules="required|maxWords:525|displayMaxWords:500"
        />
      </fieldset>

      <hr>

      <fieldset class="supporting-materials">
        <h3 class="supporting-materials-heading">
          Supporting Materials
        </h3>
        <p>The following materials will be scored by judges for quality and clarity and contribute to the nomination's overall final score.</p>
        <DSTextArea
          v-model="formData.media_links"
          :value="formData.media_links"
          label="Media Links"
          :required="false"
          rules="maxLines:5"
          additional-info="Add one link per line to relevant media such as videos, press coverage, etc. that will assist the judges in evaluating your entry. (Limit 5)"
        />

        <h4 class="upload-files-heading">
          Upload files
        </h4>

        <p>
          Upload up to five files.
        </p>
        <p>Suggested Materials</p>
        <ul class="suppporting-materials-list">
          <li>Organization Annual Reports</li>
          <li>Quantified Data (success metrics)</li>
          <li>Marketing and/or Presentation Materials (slide deck, brochures, flyers)</li>
          <li>Photographs and/or Video</li>
          <li>Success Stories and Case Studies</li>
          <li>Testimonials and Letters of Support</li>
        </ul>
        <p>
          <em>{{ filesTypeNote }}</em>
        </p>
        <p class="file-upload-notice">
          <strong>You are <em>not</em> required to upload <em>all</em> these types of files.</strong>
        </p>

        <div v-if="editMode && uploadedFiles.length > 0">
          <h3>Previously Attached Files</h3>
          <NominationFiles
            :files="uploadedFiles"
            @remove="removeFile"
          />
        </div>

        <DSFileUpload
          ref="fileUpload"
          :button-labels="fileUploadLabels"
          :previously-uploaded-files="uploadedFiles"
          :allowed-file-types="filesAllowedFileTypes"
          @upload-files="uploadMore"
        />

        <h3 v-if="editMode && uploadedLogos.length > 0">
          Previously Uploaded Logos
        </h3>
        <NominationFiles
          v-if="editMode && uploadedLogos.length > 0"
          :files="uploadedLogos"
          @remove="removeLogo"
        />
        <DSFileUpload
          ref="logoUpload"
          :button-labels="logoUploadLabels"
          :allowed-file-types="logoAllowedFileTypes"
          :is-logo="true"
          :max-files="3"
          :label="'Please include up to 3 logos of the associated non-profit organization'"
          :previously-uploaded-files="uploadedLogos"
        >
        </DSFileUpload>
      </fieldset>

      <fieldset style="display: flex; gap: 10px;">
        <DSButton
          color="primary"
          type="submit"
          :invalid="invalid"
        >
          Save Nomination
        </DSButton>
        <DSButton
          v-if="!submitted"
          color="primary"
          type="button"
          :invalid="invalid"
          @click.native="submitAndPay"
        >
          Submit & Pay
        </DSButton>
      </fieldset>
    </template>
  </DSForm>
</template>

<script>
import { getAwardTitles, getOrganizationSizes, getAwardNoOrganizationSizes, getParsedAwardData, removeFile, getAwards } from '../services/HttpSvc'
import * as DSFormComponents from './forms'
import NominationFiles from './NominationFiles'
import { USER_NOMINATIONS } from '../core/constants'
import DSTextArea from "./forms/DSTextArea";
export default {
  name: 'NominateForm',

  components: {
    DSTextArea,
    ...DSFormComponents,
    NominationFiles,
  },

  props: {
    submitLabel: {
      type: String,
      default: '',
    },
    nominationId: {
      type: Number,
      default: () => -1,
    },
    prefillFormData: {
      type: Object,
      default: () => {
        return {
          nominee_address: '',
          nominee_first_name: '',
          nominee_last_name: '',
          nominee_city: '',
          nominee_country: '',
          nominee_state: '',
          nominee_zip_code: '',
          nominee_email: '',
          nominee_organization: '',
          organization_size_id: '',
          organization_size_name: '',
          award_id: '',
          organization_name: '',
          title: '',
          description: '',
          url: '',
          non_profit_name: '',
          non_profit_website: '',
          media_links: '',
          outcomes: '',
          innovation: '',
          hope_story: '',
        }
      },
    },
    uploadedFiles: {
      type: Array,
      default: () => [],
    },
    uploadedLogos: {
      type: Array,
      default: () => [],
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    submitted: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      awardData: {},
      awards: [],
      formData: {},
      awardOptions: {},
      orgSizeOptions: {},
      awardNoOrganizationSizes: {},
      fileUploadLabels: [
        'Files',
      ],
      logoUploadLabels: [
        'Logo',
      ],
      filesAllowedFileTypes: ['.jpg', '.jpeg', '.png', '.pdf', '.doc', '.docx', '.ppt', '.pptx', '.mp4', '.mov'],
      logoAllowedFileTypes: ['.jpg', '.jpeg', '.png', '.pdf'],
      isSubmitAndPay: false,
    }
  },

  computed: {

    editMode() {
      return this.nominationId > 0
    },

    filesTypeNote() {
      return 'Supported file types: ' + this.filesAllowedFileTypes.join(', ')
    },

    selectedAward(){
      const award_id = this.formData.award_id;
      if (typeof award_id === 'undefined' || award_id === ''){
        return null
      }

      return this.awards.find(a => a.id.toString() === award_id);
    },

    nomineeType() {
      if (!this.selectedAward){
        return null
      }

      if (this.selectedAward.is_organization){
        return 'Organization';
      }
      return 'Person';
    },

    hasSizes(){
      if(this.selectedAward) {
        return !this.selectedAward.no_organization_size;
      }
      return null;
    },
  },

  watch: {
    prefillFormData: {
      immediate: true,
      handler(formData) {
        this.setData(formData)
      },
    },
  },

  async created() {
    const [awardData, organizationSizes] = await Promise.all([getAwards(), getOrganizationSizes()]);
    this.awardData = awardData;
    this.awards = getParsedAwardData(awardData);
    const dropdownRequests = [getAwardTitles(awardData), organizationSizes, getAwardNoOrganizationSizes(awardData)];
    if (this.prefillFormData) {
      this.setData(this.prefillFormData)
    }
    [this.awardOptions, this.orgSizeOptions, this.awardNoOrganizationSizes] = dropdownRequests
  },

  methods: {
    setData(formData) {
      if (formData.organization_size_name) {
        formData.organization_size_name = formData.organization_size_name.toString()
      }
      if(formData.award_id){
        formData.award_id = formData.award_id.toString()
      }
      // Convert null values to empty strings for form fields
      if (formData.innovation === null || formData.innovation === undefined) {
        formData.innovation = ''
      }
      if (formData.hope_story === null || formData.hope_story === undefined) {
        formData.hope_story = ''
      }
      if (formData.outcomes === null || formData.outcomes === undefined) {
        formData.outcomes = ''
      }
      this.formData = { ...formData }
    },
    cancel() {
      window.location = USER_NOMINATIONS
    },
    async uploadFiles(nominationId = this.nominationId) {
      const [fileResult, logoResult] = await Promise.all([
        this.$refs.fileUpload.uploadFiles(nominationId),
        this.$refs.logoUpload.uploadFiles(nominationId)
      ])
      return {
        failed: [...(fileResult?.failed || []), ...(logoResult?.failed || [])],
        successful: [...(fileResult?.successful || []), ...(logoResult?.successful || [])]
      }
    },
    async uploadMore() {
      await this.$refs.fileUpload.uploadFiles(this.nominationId)
      this.setData(this.prefillFormData)
      this.$emit('files-changed')
    },
    removeFile(file) {
      removeFile(file.id)
      this.uploadedFiles.splice(this.uploadedFiles.indexOf(file), 1)
    },
    async removeLogo(logo) {
      await removeFile(logo.id)
      this.$emit('files-changed')
    },
    handleSubmit(formData) {
      // Always use NominateForm's formData, not DSForm's
      const data = this.formData
      if (this.isSubmitAndPay) {
        this.isSubmitAndPay = false
        this.$emit('submit-and-pay', data)
      } else {
        this.$emit('submit', data)
      }
    },
    submitAndPay() {
      // Set flag and trigger native form submission
      this.isSubmitAndPay = true
      // Programmatically submit the form element, which will trigger validation
      this.$refs.dsForm.$el.querySelector('form').requestSubmit()
    },
  },

}
</script>

<style lang="scss" scoped>
  @import '../../app/assets/stylesheets/variables.scss';

  fieldset.supporting-materials {
    p {
      padding-bottom: 8px;
    }
  }

  .narrative-questions-heading, .upload-files-heading {
    padding-top: 1rem;
    padding-bottom: 1rem;
  }

  .supporting-materials-heading, .narrative-questions-heading {
    font-weight: 500;
    padding-bottom: 1rem;
  }

  .file-upload-notice {
    padding-bottom: 1rem;
  }

  ul.suppporting-materials-list {
    padding-bottom: 5px;
    list-style: disc;
    padding-left: 1rem;

    li {
      color: var(--purple-2);
      padding-left: unset;

      &::before {
        display: none;
      }
    }
  }
</style>
